import { ChangeDetectorRef, Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatStepper } from '@angular/material/stepper';
import { enableDebugTools } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { FirebaseService } from 'src/app/commons/firebase.service';
import { FunctionsService } from 'src/app/data/functions.service';
import { RegistroClienteComponent } from '../cliente/registro/registro-cliente.component';
import { Location } from 'src/app/core/models/configurations';
import { DataService } from 'src/app/data/data.service';

@Component({
  selector: 'app-negociacao',
  templateUrl: './adiciona-negociacao.component.html',
  styleUrls: ['./adiciona-negociacao.component.scss']
})
export class AdicionaNegociacaoComponent implements OnInit {
  @ViewChild('negociacaoStepper') negociacaoStepper: MatStepper;
  @ViewChild(RegistroClienteComponent) registroClienteComponent: RegistroClienteComponent;
  public loading: boolean = false;
  public negociacaoFormGroup: UntypedFormGroup;
  public usuarioEstaLogado: boolean;
  public usuarioJaTemUmaConta: boolean;
  public negociacao = null;
  public showRegionalization: boolean = false;
  searchTerm: string = '';
  locations: Location[] = [];
  location: GeolocationPosition | null = null;
  nearestCity: string | null = null;
  city: string | null = null;
  errorMessage: string | null = null;
  showDropdown: boolean = false;

  public condicoesProdutos = [
    { label: 'Novo', codigo: 'novo' },
    { label: 'Usado', codigo: 'usado' }
  ]

  public duracoes = [
    { label: '1 Dia', codigo: '1' },
    { label: '3 Dias', codigo: '3' },
    { label: '5 Dias', codigo: '5' },
    { label: '7 Dias', codigo: '7' }
  ]

  constructor(
    private router: Router,
    private cdr: ChangeDetectorRef, // Injete ChangeDetectorRef
    public dialogRef: MatDialogRef<any>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public firebase: FirebaseService,
    private formBuilder: UntypedFormBuilder,
    private functions: FunctionsService,
    private dataService: DataService
  ) { }

  ngOnInit(): void {
    this.usuarioJaTemUmaConta = false;
    this.usuarioEstaLogado = !!this.firebase?.user;

    // Pré-preenchimento vindo de "compre novamente" (duplicação de uma negociação
    // anterior). Quando ausente, o formulário nasce vazio exatamente como hoje.
    const duplicacao = this.data?.duplicacao;

    this.negociacaoFormGroup = this.formBuilder.group({
      condicao: [{ value: this.condicaoInicial(duplicacao), disabled: false }, [
        Validators.required
      ]],
      // A duração original da compra anterior vem em duplicacao.duracao
      // (GET /negociacoes/v1/:uid, campo duracao). Documentos atuais trazem
      // string '1'|'3'|'5'|'7'; legados podem vir como número ou sem o campo,
      // e enquanto a API antiga estiver em staging o campo nem chega — nesses
      // casos caímos no maior prazo disponível ('7' dias) como default. O
      // campo continua livremente editável pelo comprador.
      duracao: [{ value: this.duracaoInicial(duplicacao), disabled: false }, [
        Validators.required
      ]],
      quantidade: [{ value: duplicacao?.quantidade ?? 1, disabled: false }, [
        Validators.required,
        Validators.min(1),
        Validators.max(100)
      ]],
      valor_pretendido: [{ value: duplicacao?.valor_pretendido ?? '', disabled: false }, [
        Validators.min(1),
        Validators.max(999999.99)
      ]],
      encerrar_automaticamente: [{ value: duplicacao?.encerrar_automaticamente ?? false, disabled: false }, [
        Validators.required
      ]]
    });

    if (duplicacao?.negotiations_region?.length) {
      // Região vinda da negociação anterior: populamos e marcamos como
      // selecionada diretamente, SEM chamar getLocation(). Chamar getLocation()
      // aqui criaria uma race condition — a resposta assíncrona de geolocalização
      // sobrescreveria this.locations e perderia essa pré-seleção.
      this.locations = duplicacao.negotiations_region.map((location: Location) => ({
        ...location,
        selected: true
      }));
      this.showRegionalization = true;
    } else {
      this.getLocation();
    }
  }

  private condicaoInicial(duplicacao: any): string {
    if (!duplicacao) {
      return '';
    }
    if (duplicacao.novo) {
      return 'novo';
    }
    if (duplicacao.usado) {
      return 'usado';
    }
    return '';
  }

  private duracaoInicial(duplicacao: any): string {
    if (!duplicacao) {
      return '';
    }
    const duracao = String(duplicacao.duracao);
    if (['1', '3', '5', '7'].includes(duracao)) {
      return duracao;
    }
    return '7';
  }

  async searchLocations(event?: Event): Promise<void> {
    this.functions.searchLocations({searchTerm: this.searchTerm.trim(), selectedLocations: this.selectedLocations}).subscribe({
      next: (response) => {
        if (response.body.data) {
          this.locations = response.body.data;
          this.showDropdown = true; // Garanta que o dropdown seja exibido após a busca
          this.cdr.detectChanges(); // Forçar a detecção de mudanças
        }
      },
      error: (err) => {
        console.log(err)
      }
    })
  }

  getLocation(): void {
    this.dataService.getCurrentPosition().subscribe({
      next: (position: GeolocationPosition) => {
        this.location = position;
        this.errorMessage = null;
        console.log('Localização:', position.coords.latitude.toFixed(4), position.coords.longitude.toFixed(4));
        const lat = Number(position.coords.latitude.toFixed(4));
        const lng = Number(position.coords.longitude.toFixed(4));

        this.functions.searchNearestCities({ lat, lng }).subscribe({
          next: (res) => {
            this.locations = res.body.data;
            const cityData = res.body?.data?.filter?.(f => f?.type === 'city')?.[0];
            const stateData = res.body?.data?.filter?.(f => f?.stateId === cityData?.stateId)?.[0];
            const countryData = res.body?.data?.filter?.(f => f?.countryId === stateData?.countryId)?.[0];
            this.city = cityData?.name;
            this.nearestCity = `${cityData?.name}, ${stateData?.name} (estado), ${countryData?.name}`;
            this.showDropdown = true; // Garanta que o dropdown seja exibido após a busca
            this.cdr.detectChanges(); // Forçar a detecção de mudanças
          },
          error: (err) => {
            console.log(err);
          }
        });
      },
      error: (error: any) => {
        this.errorMessage = this.handleError(error);
        // this.location = null;
        this.searchLocations();
      }
    })
  }

  private handleError(error: GeolocationPositionError | Error | any): string {
    switch (error?.code) {
      case 1:
        return 'Permissão para geolocalização foi negada.';
      case 2:
        return 'Localização indisponível.';
      case 3:
        return 'Tempo limite para obter localização excedido.';
      default:
        return 'Erro ao obter localização: ' + error.message;
    }
  }

  closeDropdown(): void {
    this.showDropdown = false;
  }

  onSearch(event?: Event): void {
    if (event) {
      const target = event.target as HTMLInputElement;
      this.searchTerm = target.value;
    }
    this.showDropdown = true;
  }

  toggleDropdown(): void {
    this.showDropdown = !this.showDropdown;
  }

  toggleRegionalization(): void {
    this.showRegionalization = !this.showRegionalization;
  }
  toggleLocation(location: Location): void {
    // Verificar se é a última localização selecionada
    if (location.selected && this.selectedLocations.length === 1) {
      // Não permitir desmarcar se for a única selecionada
      return;
    }

    switch (location.type) {
      case 'country':
        this.toggleCountry(location);
        break;
      case 'state':
        this.toggleState(location);
        break;
      case 'city':
        this.toggleCity(location);
        break;
    }
  }


  private toggleCountry(country: Location): void {
    country.selected = !country.selected;

    if (country.selected) {
      // Se o país foi selecionado, desselecionar todos os estados e cidades desse país
      this.locations
        .filter(loc =>
          (loc.type === 'state' && loc.countryId === country.id) ||
          (loc.type === 'city' && loc.countryId === country.id)
        )
        .forEach(location => location.selected = false);
    }
  }

  private toggleState(state: Location): void {
    state.selected = !state.selected;

    if (state.selected) {
      // Se o estado foi selecionado:
      // 1. Desselecionar o país correspondente
      const country = this.locations.find(loc =>
        loc.type === 'country' && loc.id === state.countryId
      );
      if (country) {
        country.selected = false;
      }

      // 2. Desselecionar todas as cidades desse estado
      this.locations
        .filter(loc => loc.type === 'city' && loc.stateId === state.id)
        .forEach(city => city.selected = false);
    }
  }

  private toggleCity(city: Location): void {
    city.selected = !city.selected;

    if (city.selected) {
      // Se a cidade foi selecionada:
      // 1. Desselecionar o país correspondente
      const country = this.locations.find(loc =>
        loc.type === 'country' && loc.id === city.countryId
      );
      if (country) {
        country.selected = false;
      }

      // 2. Desselecionar o estado correspondente
      const state = this.locations.find(loc =>
        loc.type === 'state' && loc.id === city.stateId
      );
      if (state) {
        state.selected = false;
      }
    }
  }

  getLocationIcon(location: Location): string {
    switch (location.type) {
      case 'country':
        return 'public';
      case 'state':
        return 'location_on';
      case 'city':
        return 'apartment';
      default:
        return 'place';
    }
  }

  getLocationDescription(location: Location): string {
    switch (location.type) {
      case 'country':
        return location.name;

      case 'state':
        const country = this.locations.find(loc =>
          loc.type === 'country' && loc.id === location.countryId
        );
        return `${location.name} (estado), ${country?.name || ''}`;

      case 'city':
        const state = this.locations.find(loc =>
          loc.type === 'state' && loc.id === location.stateId
        );
        const cityCountry = this.locations.find(loc =>
          loc.type === 'country' && loc.id === location.countryId
        );
        return `${location.name}, ${state?.name || ''} (estado), ${cityCountry?.name || ''} `;

      default:
        return location.name;
    }
  }

  removeLocation(location: Location): void {
    // Não permitir remover se for a única localização selecionada
    if (this.selectedLocations.length === 1) {
      return;
    }

    location.selected = false;
  }

  get selectedLocations(): Location[] {
    return this.locations.filter(location => location.selected);
  }

  get filteredLocations(): Location[] {
    return this.locations.filter(location =>
      location.name.toLowerCase().includes(this.searchTerm.toLowerCase())
    );
  }

  registroFinalizado(resultado: string) {
    if (resultado === 'login') {
      this.usuarioJaTemUmaConta = true;
      this.usuarioEstaLogado = false;
    } else if (resultado === 'finalizado') {
      this.negociacaoStepper.next();
    }
  }

  acessoFinalizado(resultado: string) {
    if (resultado === 'registrar') {
      this.usuarioJaTemUmaConta = false;
      this.usuarioEstaLogado = false;
    } else if (resultado === 'finalizado') {
      this.negociacaoStepper.next();
    }
  }

  irParaNegociacao() {
    this.router.navigateByUrl(`/negociacao/${this.negociacao}`);
    this.dialogRef.close();
  }

  iniciaNegociacao() {
    if (this.negociacaoFormGroup.invalid) {
      this.negociacaoFormGroup.updateValueAndValidity();
      this.negociacaoFormGroup.markAllAsTouched();
      alert('Por favor, preencher todos os campos obrigatórios');
      return
    }

    const payload: any = {
      novo: false,
      usado: false,
    };

    // A região é opcional: só a enviamos quando o comprador escolher explicitamente
    // um ou mais locais. Sem seleção, o campo é omitido para que o backend herde a
    // região cadastrada no perfil do cliente (negotiations_region ?? cliente.negotiations_region).
    if (this.selectedLocations.length) {
      payload.negotiations_region = this.selectedLocations;
    }

    Object.keys(this.negociacaoFormGroup.controls).forEach(key => {
      payload[key] = this.negociacaoFormGroup.controls[key].value;
    });

    payload[this.negociacaoFormGroup.controls['condicao'].value] = true;

    this.loading = true;
    this.negociacaoFormGroup.disable();

    if (payload.valor_pretendido === '') {
      payload.valor_pretendido = 0;
    }

    payload.ean = this.data.ean;

    this.functions.addNegociacao(payload).toPromise()
      .then(res => {
        this.negociacao = res.body.data;
        this.negociacaoStepper.next();
      })
      .catch(error => {
        console.error(error);

        alert('Ops, não conseguimos finalizar a negociação. Por favor, tente mais tarde.');

      })
      .finally(() => {
        this.loading = false;
        this.negociacaoFormGroup.enable();
      })
  }
}

